/**
 * Inline predictive search for the header. Sits in the top row between the
 * logo and the action buttons, and shows product / collection matches in a
 * dropdown as the customer types, backed by Shopify's predictive search API
 * (/search/suggest.json). Submitting the form falls through to /search, so it
 * still works if the fetch fails or JavaScript is off.
 *
 * Progressive enhancement, like brand-header.js: if the header column is ever
 * renamed this no-ops and Horizon's own search icon keeps working.
 */
(() => {
  const root = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '');
  const SUGGEST_URL = `${root}/search/suggest.json`;
  const SEARCH_URL = `${root}/search`;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 220;

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const money = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat((window.Shopify && Shopify.locale) || 'en', { style: 'currency', currency }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };

  // Shopify CDN images accept a width param; keep the cache-busting v= intact.
  const thumb = (url) => (url ? `${url}${url.includes('?') ? '&' : '?'}width=96` : '');

  // The placeholder types through what people actually search for, so the box
  // reads as live rather than as an empty field. Off for reduced-motion users.
  const PHRASES = ['embroidered patches', 'PVC patches', 'chenille patches', 'leather patches', 'keychains', 'woven patches', 'stickers', 'hat patches'];
  const STATIC_PLACEHOLDER = 'Search patches, styles, keychains…';
  function typewriter() {
    if (window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let word = 0, pos = 0, deleting = false, timer = 0;
    const tick = () => {
      const input = document.querySelector('.mcp-search__input');
      if (!input) { timer = setTimeout(tick, 1500); return; }
      if (document.activeElement === input || input.value) {
        input.placeholder = STATIC_PLACEHOLDER;
        timer = setTimeout(tick, 900);
        return;
      }
      const phrase = PHRASES[word];
      pos += deleting ? -1 : 1;
      input.placeholder = `Search ${phrase.slice(0, pos)}${deleting || pos < phrase.length ? '|' : '…'}`;
      let delay = deleting ? 32 : 68;
      if (!deleting && pos === phrase.length) { delay = 1700; deleting = true; }
      else if (deleting && pos === 0) { deleting = false; word = (word + 1) % PHRASES.length; delay = 320; }
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, 1400);
    document.addEventListener('visibilitychange', () => {
      clearTimeout(timer);
      if (!document.hidden) timer = setTimeout(tick, 600);
    });
  }

  const searchIcon =
    '<svg class="mcp-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';

  function render(panel, query, results) {
    const products = (results.products || []).slice(0, 6);
    const collections = (results.collections || []).slice(0, 4);
    const seeAll = `<a class="mcp-search__all" href="${SEARCH_URL}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last">See all results for &ldquo;${esc(query)}&rdquo; &rarr;</a>`;

    if (!products.length && !collections.length) {
      panel.innerHTML = `<p class="mcp-search__empty">No matches for &ldquo;${esc(query)}&rdquo; yet &mdash; try a patch type like <b>embroidered</b> or <b>PVC</b>.</p>${seeAll}`;
      return;
    }

    let html = '';
    if (products.length) {
      html += '<p class="mcp-search__label">Products</p>';
      html += products
        .map((p) => {
          const min = p.price_min ?? p.price;
          const from = p.price_max != null && Number(p.price_max) > Number(min) ? 'From ' : '';
          const img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          return (
            `<a class="mcp-search__item" role="option" href="${esc(p.url)}">` +
            (img ? `<img class="mcp-search__thumb" src="${esc(thumb(img))}" alt="" width="44" height="44" loading="lazy">` : '<span class="mcp-search__thumb"></span>') +
            `<span><span class="mcp-search__title">${esc(p.title)}</span>` +
            `<span class="mcp-search__price">${from}${money(min)}</span></span></a>`
          );
        })
        .join('');
    }
    if (collections.length) {
      html += '<p class="mcp-search__label">Categories</p><div class="mcp-search__pills">';
      html += collections
        .map((c) => `<a class="mcp-search__pill" role="option" href="${esc(c.url)}">${esc(c.title)}</a>`)
        .join('');
      html += '</div>';
    }
    panel.innerHTML = html + seeAll;
  }

  // Per-form state lives on the element, and every listener is delegated from
  // the document, so the box keeps working even if the theme re-renders the
  // header and swaps the nodes underneath us.
  const stateOf = (form) => (form.__mcp ||= { timer: 0, controller: null, lastQuery: '' });
  const refs = (form) => ({ input: form.querySelector('.mcp-search__input'), panel: form.querySelector('.mcp-search__panel') });
  const open = (form) => { const { input, panel } = refs(form); panel.hidden = false; input.setAttribute('aria-expanded', 'true'); };
  const close = (form) => { const { input, panel } = refs(form); panel.hidden = true; input.setAttribute('aria-expanded', 'false'); };

  async function lookup(form, query) {
    const st = stateOf(form);
    const { input, panel } = refs(form);
    if (st.controller) st.controller.abort();
    st.controller = new AbortController();
    if (!panel.innerHTML || panel.hidden) { panel.innerHTML = '<p class="mcp-search__empty">Searching&hellip;</p>'; open(form); }
    const url = new URL(SUGGEST_URL, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    try {
      const res = await fetch(url, { signal: st.controller.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (input.value.trim() !== query) return; // stale
      st.lastQuery = query;
      render(panel, query, (data.resources && data.resources.results) || {});
      open(form);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // Say so rather than silently closing; Enter still submits to /search.
      panel.innerHTML = `<p class="mcp-search__empty">Suggestions are unavailable right now (${esc(err.message || err)}). Press Enter to search.</p>`;
      open(form);
    }
  }

  function onType(form) {
    const st = stateOf(form);
    const { input, panel } = refs(form);
    clearTimeout(st.timer);
    const q = input.value.trim();
    if (q.length < MIN_CHARS) { close(form); return; }
    if (q === st.lastQuery && !panel.hidden) return;
    st.timer = setTimeout(() => lookup(form, q), DEBOUNCE_MS);
  }

  const formFor = (target) => target && target.closest ? target.closest('.mcp-search') : null;
  document.addEventListener('input', (e) => { const f = formFor(e.target); if (f && e.target.matches('.mcp-search__input')) onType(f); });
  document.addEventListener('keyup', (e) => { const f = formFor(e.target); if (f && e.target.matches('.mcp-search__input')) onType(f); });
  document.addEventListener('focusin', (e) => {
    const f = formFor(e.target);
    if (!f || !e.target.matches('.mcp-search__input')) return;
    const st = stateOf(f);
    if (st.lastQuery && e.target.value.trim() === st.lastQuery) open(f);
  });
  // Arrow keys walk the results; Escape closes; Enter on a result follows it.
  document.addEventListener('keydown', (e) => {
    const f = formFor(e.target);
    if (!f) return;
    const { input, panel } = refs(f);
    if (e.key === 'Escape') { close(f); input.focus(); return; }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const options = [...panel.querySelectorAll('[role="option"], .mcp-search__all')];
    if (panel.hidden || !options.length) return;
    e.preventDefault();
    const i = options.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
    options[next].focus();
  });
  document.addEventListener('pointerdown', (e) => {
    document.querySelectorAll('.mcp-search').forEach((f) => { if (!f.contains(e.target)) close(f); });
  });

  function mount() {
    const col = document.querySelector('.header__column--right');
    if (!col || col.querySelector('.mcp-search')) return;

    const form = document.createElement('form');
    form.className = 'mcp-search';
    form.setAttribute('role', 'search');
    form.action = SEARCH_URL;
    form.method = 'get';
    form.autocomplete = 'off';
    form.innerHTML =
      searchIcon +
      '<input class="mcp-search__input" type="search" name="q" placeholder="' + STATIC_PLACEHOLDER + '" ' +
      'aria-label="Search products" aria-autocomplete="list" aria-controls="mcp-search-results" aria-expanded="false" enterkeyhint="search">' +
      '<input type="hidden" name="options[prefix]" value="last">' +
      '<div class="mcp-search__panel" id="mcp-search-results" role="listbox" aria-label="Search suggestions" hidden></div>';

    // Sit between the logo and the Chat / Login buttons when those are present.
    const actions = col.querySelector('.mcp-header-actions');
    if (actions) actions.before(form);
    else col.prepend(form);
  }

  // If the theme re-renders the header column (editor, morphs), put the box back.
  const observer = new MutationObserver(() => {
    clearTimeout(observer._t);
    observer._t = setTimeout(mount, 150);
  });
  if (document.body) observer.observe(document.body, { childList: true, subtree: true });

  const start = () => { mount(); typewriter(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  document.addEventListener('shopify:section:load', mount);
})();
